import React, { useEffect } from 'react';
import {
    FileSignature, ShieldCheck, CreditCard, ClipboardList, Video, LayoutGrid,
    Clock, CheckCircle2, Zap, MoveRight, Download, ExternalLink,
} from 'lucide-react';

/*
 * Abhinav Malhotra / Abhifit Lifestyle Coaching LLC — client onboarding hub.
 * Generated from .claude/skills/onboard-client/onboarding-template.jsx.
 * Route: /abhinav-onboarding
 * All step links (SLA, NDA, invoice, questionnaire, training-data Drive, Content OS) are live.
 */

const NOTION_CONTENT_OS = 'https://app.notion.com/p/3803ac2a530981b3a30df8456c5d2d91';
const QUESTIONNAIRE_URL = 'https://docs.google.com/document/d/1oQw5xQRMWhRAyxmLuVOd9PA8CmEUoFnB_xZ9WgZk0f4/edit';
const DRIVE_REFERENCE_URL = 'https://drive.google.com/drive/folders/1sh4_G0gAFi9UO2rcarQk1JcNCrfkt8L9?usp=sharing';

const data = {
    client: 'Abhinav',
    brandLabel: 'AbhiFit',
    pageTitle: 'Onboarding | AbhiFit via SocialSEO',
    badge: 'Ready to Launch',
    headline: 'Welcome to the Machine.',

    greeting: 'Hey Abhinav,',
    intro: [
        'Great to have you in. This page is your single home base for getting started. Everything you need to do in the first few days is laid out below as a short checklist.',
        'A quick note on how the engagement runs. The first 7 days are onboarding: we deploy your Notion content engine, lock the HNI positioning and recording cadence, and pin your AI clone. Your 3-month clock does not start on signing, it starts the day your first video goes live. That keeps the heavy upfront setup off your billing window.',
        'Work down the steps below. Once the documents are signed and the invoice is cleared, we move straight into production.',
    ],

    // The onboarding checklist — the core of the page.
    steps: [
        {
            icon: FileSignature,
            title: 'Review & sign your agreement and NDA',
            desc: 'Please read both properly so we are fully aligned on deliverables and on the confidentiality around the AI-clone production. We need both signed before any production work begins.',
            links: [
                { label: 'Service Agreement (SLA)', href: '/assets/abhinav/abhinav_agreement.pdf', icon: Download },
                { label: 'Mutual NDA', href: '/assets/abhinav/abhinav_nda.pdf', icon: Download },
            ],
        },
        {
            icon: CreditCard,
            title: 'Clear your Month 1 invoice',
            desc: 'Invoice SSEO021, ₹1,43,044 for Month 1 of 3, due 17 June 2026. Payable by bank transfer. Our full account details are printed on the invoice itself. If you wire internationally, please set the transfer to the "OUR" charge code and process directly in INR so the correct amount reaches us.',
            links: [
                { label: 'Download Invoice (bank details inside)', href: '/assets/abhinav/SSEO02110062026.pdf', icon: Download },
            ],
        },
        {
            icon: ClipboardList,
            title: 'Fill your Founder Questionnaire',
            desc: 'This is how we lock your positioning, voice, story, and content pillars. Open the template, go to File and Make a copy, fill it in, then share your copy back with us at info@abhifit.com. It takes about 20 minutes, and the sharper your answers, the sharper the content.',
            links: [
                { label: 'Open the Questionnaire Template', href: QUESTIONNAIRE_URL, icon: ExternalLink },
            ],
        },
        {
            icon: Video,
            title: 'Send your AI-clone training data',
            desc: 'This is what we use to build your AI clone and seed your content. Set up one Drive folder organized exactly like the example below, then share it with us at info@abhifit.com. Here is everything we need:',
            links: [
                { label: 'See the Example Folder Structure', href: DRIVE_REFERENCE_URL, icon: ExternalLink },
            ],
            tips: [
                'Audio: up to 2 hours of clear, solo audio of you speaking. This is what the voice clone learns from, so more is better.',
                'Video: at least 3 minutes of you on camera, ideally across a few different outfits and, where possible, different settings.',
                'Portraits: a set of clean portrait photos we will use for your thumbnails.',
                'B-roll (optional): a separate folder where you can drop session clips, lifestyle, or any extra footage.',
                'Keep everything in one folder, organized like the example, then share it to info@abhifit.com.',
            ],
        },
        {
            icon: LayoutGrid,
            title: 'Open your Content OS',
            desc: 'Your Notion workspace is where the whole engine lives: scripts pipeline, posting calendar, ICP tracker, and your daily DM digest. This is where you give inputs and approve content.',
            links: [
                { label: 'Open AbhiFit Content OS', href: NOTION_CONTENT_OS, icon: ExternalLink },
            ],
        },
    ],

    timelineTitle: 'What the 3 months look like',
    phases: [
        {
            title: 'Onboarding & Setup',
            window: 'Days 1–7',
            items: [
                'Documents signed, Month 1 invoice cleared, questionnaire in.',
                'We deploy your Notion Content OS and lock the HNI positioning and recording cadence.',
                'We train and pin your AI clone, and you approve the first 7 scripts.',
            ],
        },
        {
            title: 'Launch',
            window: 'Day 8',
            items: [
                'First videos go live across Instagram, YouTube, LinkedIn, Facebook, and X. Your 3-month clock starts here.',
                'Hook variants calibrated for your UAE HNI audience.',
            ],
        },
        {
            title: 'Month 1 Production',
            window: 'Weeks 1–4',
            items: [
                '30 videos and 30 carousels shipped, plus stories, across all five platforms.',
                'AI-clone and your real footage blended; best-performing hooks identified.',
                'First in-ICP HNI DMs surface in your daily digest.',
            ],
        },
        {
            title: 'Funnel & Rebrand',
            window: 'Month 2',
            items: [
                'Profile and website rebrand goes live; the sales funnel is rebuilt.',
                'IG-DM nurture funnel and appointment-setter automation installed (your team runs the calls).',
                'Around 60 videos cumulative; the system starts compounding.',
            ],
        },
        {
            title: 'Compound & Review',
            window: 'Month 3',
            items: [
                'Full system optimized; around 90 videos shipped across the term.',
                'Monthly direction review. Renewal conversation at a rate to be agreed.',
            ],
        },
    ],
    timelineNote:
        'Onboarding is the heavy-lift week. Once your clone is pinned and the Notion engine is live, production runs like clockwork and turnaround tightens significantly.',

    closing: {
        line1: 'Let me know if you have any questions on the documents, the questionnaire, or the recording setup.',
        line2: 'Knock out the checklist and we can get the infrastructure live today!',
    },
};

export default function AbhinavOnboarding() {
    useEffect(() => {
        document.title = data.pageTitle;
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="font-sans leading-relaxed text-white overflow-x-hidden selection:bg-indigo-500/30 pb-20 pt-24 relative z-10 min-h-screen">
            {/* Background Gradient */}
            <div className="fixed inset-0 min-h-screen bg-[#080B11] -z-20 overflow-hidden">
                <div className="absolute top-[-20%] left-[-10%] w-[60%] h-[60%] bg-indigo-600/20 blur-[160px] rounded-full mix-blend-screen pointer-events-none" />
                <div className="absolute bottom-[-20%] right-[-10%] w-[60%] h-[60%] bg-emerald-600/15 blur-[160px] rounded-full mix-blend-screen pointer-events-none" />
                <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 mix-blend-overlay pointer-events-none" />
            </div>

            {/* Header */}
            <header className="fixed top-0 left-0 right-0 z-50 p-6 flex justify-between items-center bg-[#07090E]/80 backdrop-blur-lg border-b border-white/5">
                <div className="flex items-center gap-3 w-full max-w-5xl mx-auto">
                    <span className="font-bold tracking-tight text-white text-xl">Social<span className="text-indigo-400">SEO</span></span>
                    <span className="text-white/20 mx-2">/</span>
                    <span className="text-white/80 font-medium">{data.brandLabel} Onboarding</span>
                </div>
            </header>

            <main className="max-w-4xl mx-auto px-4 pt-10 space-y-16">

                {/* Intro */}
                <section>
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm font-bold tracking-wide uppercase mb-6">
                        <CheckCircle2 className="w-4 h-4" /> {data.badge}
                    </div>

                    <h1 className="text-4xl md:text-6xl font-black tracking-tight mb-8 bg-clip-text text-transparent bg-gradient-to-r from-white via-indigo-200 to-indigo-400 leading-tight">
                        {data.headline}
                    </h1>

                    <div className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8 space-y-4 text-white/80 text-lg leading-relaxed shadow-xl">
                        <p><strong>{data.greeting}</strong></p>
                        {data.intro.map((para, i) => <p key={i}>{para}</p>)}
                    </div>
                </section>

                {/* Onboarding checklist */}
                <section>
                    <h2 className="text-2xl font-bold mb-8 flex items-center gap-3">
                        <CheckCircle2 className="text-emerald-400 w-6 h-6" /> Your First Steps
                    </h2>

                    <div className="space-y-5">
                        {data.steps
                            .filter((step) => !step.links.some((l) => l.href.includes('_PENDING')))
                            .map((step, i) => {
                            const StepIcon = step.icon;
                            return (
                                <div key={i} className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-7 shadow-xl hover:border-indigo-500/40 transition-colors">
                                    <div className="flex items-start gap-4">
                                        <div className="shrink-0 w-11 h-11 rounded-xl bg-indigo-500/15 text-indigo-400 flex items-center justify-center font-bold relative">
                                            <StepIcon className="w-5 h-5" />
                                            <span className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-indigo-500 text-white text-[11px] font-bold flex items-center justify-center border-2 border-[#0B0E14]">{i + 1}</span>
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <h3 className="text-xl font-bold text-white mb-1">{step.title}</h3>
                                            <p className="text-slate-400 text-sm leading-relaxed mb-4">{step.desc}</p>

                                            {step.tips && (
                                                <ul className="mb-4 space-y-2">
                                                    {step.tips.map((tip, t) => (
                                                        <li key={t} className="flex gap-2.5 text-slate-300 text-sm">
                                                            <Zap className="w-4 h-4 text-indigo-400 shrink-0 mt-0.5" />
                                                            <span>{tip}</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}

                                            <div className="flex flex-wrap gap-3">
                                                {step.links.map((link, l) => {
                                                    const LinkIcon = link.icon || ExternalLink;
                                                    return (
                                                        <a
                                                            key={l}
                                                            href={link.href}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-indigo-500/10 border border-indigo-500/25 text-indigo-300 font-semibold text-sm hover:bg-indigo-500/20 hover:border-indigo-400 hover:text-white transition-colors"
                                                        >
                                                            <LinkIcon className="w-4 h-4" /> {link.label} <MoveRight className="w-4 h-4" />
                                                        </a>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </section>

                {/* Timeline */}
                <section>
                    <h2 className="text-2xl font-bold mb-8 flex items-center gap-3">
                        <Clock className="text-indigo-400 w-6 h-6" /> {data.timelineTitle}
                    </h2>

                    <div className="relative border-l border-white/10 ml-4 md:ml-6 space-y-10 pb-4">
                        {data.phases.map((phase, i) => (
                            <div key={i} className="relative pl-8 md:pl-10">
                                <div className={`absolute -left-[14px] top-1 w-7 h-7 rounded-full flex items-center justify-center ${
                                    i === 0
                                        ? 'bg-indigo-500 border-[4px] border-[#07090E] shadow-[0_0_15px_rgba(99,102,241,0.5)]'
                                        : 'bg-[#1A1D24] border border-white/20'
                                }`}>
                                    <span className={`text-xs font-bold ${i === 0 ? 'text-white' : 'text-white/60'}`}>{i + 1}</span>
                                </div>
                                <h3 className="text-xl font-bold mb-1 text-white">{phase.title}</h3>
                                <p className={`text-sm font-semibold mb-3 uppercase tracking-wider ${i === 0 ? 'text-indigo-400' : 'text-indigo-400/70'}`}>
                                    {phase.window}
                                </p>
                                <ul className={`space-y-3 ${i === 0 ? 'text-slate-300' : 'text-slate-300/80'}`}>
                                    {phase.items.map((item, j) => (
                                        <li key={j} className="flex gap-3">
                                            <Zap className="w-5 h-5 text-indigo-400 shrink-0" />
                                            <span>{item}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>

                    {data.timelineNote && (
                        <div className="bg-indigo-500/10 border border-indigo-500/20 p-5 rounded-xl text-indigo-200 text-sm leading-relaxed mt-6">
                            <strong className="text-indigo-400">Note:</strong> {data.timelineNote}
                        </div>
                    )}
                </section>

                {/* Closing */}
                <section>
                    <div className="bg-gradient-to-br from-slate-900 to-[#0F172A] border border-white/10 rounded-3xl p-8 shadow-2xl relative overflow-hidden text-center">
                        <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/10 blur-[80px] rounded-full pointer-events-none" />
                        <ShieldCheck className="w-9 h-9 text-emerald-400 mx-auto mb-4" />
                        <p className="text-white/60 mb-2">{data.closing.line1}</p>
                        <p className="text-lg font-bold text-white">{data.closing.line2}</p>
                    </div>
                </section>

            </main>
        </div>
    );
}
